import React from 'react';
import { Line } from 'react-chartjs-2';
import { Activity } from 'lucide-react';
import { HealthMetrics } from '../../types/health';

interface StrainTrendsProps {
  metrics: HealthMetrics;
  timeframe: '24h' | '7d' | '30d';
  onTimeframeChange: (timeframe: '24h' | '7d' | '30d') => void;
}

export function StrainTrends({ metrics, timeframe, onTimeframeChange }: StrainTrendsProps) {
  const getLabels = () => {
    switch (timeframe) {
      case '24h':
        return Array.from({ length: 24 }, (_, i) => `${i}:00`);
      case '7d':
        return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      case '30d':
        return Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`);
    }
  };

  const data = {
    labels: getLabels(),
    datasets: [
      {
        label: 'Strain',
        data: metrics.strain.dayHistory,
        borderColor: 'hsl(var(--primary))',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          gradient.addColorStop(0, 'hsla(var(--primary), 0.2)');
          gradient.addColorStop(1, 'hsla(var(--primary), 0)');
          return gradient;
        },
        tension: 0.4,
        fill: true
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          }
        }
      },
      y: {
        min: 0,
        max: 21,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        },
        ticks: {
          color: 'rgb(156, 163, 175)',
          font: {
            size: 10
          }
        }
      }
    }
  };

  return (
    <div className="theme-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Strain Trends</h3>
        </div>
        <div className="flex gap-2">
          {(['24h', '7d', '30d'] as const).map((t) => (
            <button
              key={t}
              onClick={() => onTimeframeChange(t)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                timeframe === t
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/70'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64">
        <Line data={data} options={options} />
      </div>
    </div>
  );
}